import PageTitle from '@shared/layouts/components/page-title/page-title.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { newContactClubFields } from '@/config/contacts';
import FormProvider from '@shared/components/forms/form-provider.vue';
import Datepicker from '@shared/components/forms/inputs/datepicker/datepicker.vue';
import Input from '@shared/components/forms/inputs/input/input.vue';
import InputGroup from '@shared/components/forms/inputs/input-group/input-group.vue';
import Select from '@shared/components/forms/inputs/select/select.vue';
import Switch from '@shared/components/forms/inputs/switch/switch.vue';
import Textarea from '@shared/components/forms/inputs/textarea/textarea.vue';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import CountriesSelector from '@shared/components/forms/inputs/countries-selector/countries-selector.vue';
import Section from '@shared/components/forms/inputs/section/section.vue';
import VueRecaptcha from 'vue-recaptcha';
import moment from 'moment';
import TermsCheckbox from '@shared/components/forms/inputs/terms-checkbox/terms-checkbox.vue';

@Component({
  name: 'bpc-new-contact',
  components: {
    'bpc-page-title': PageTitle,
    'bpc-form-provider': FormProvider,
    'bpc-input': Input,
    'bpc-input-group': InputGroup,
    'bpc-select': Select,
    'bpc-textarea': Textarea,
    'bpc-datepicker': Datepicker,
    'bpc-switch': Switch,
    'bpc-entity-select': EntitySelect,
    'bpc-countries-selector': CountriesSelector,
    'bpc-section': Section,
    'vue-recaptcha': VueRecaptcha,
    'bpc-terms-checkbox': TermsCheckbox,
  },
})
export default class NewContact extends Vue {
  $refs!: {
    formProvider: InstanceType<typeof FormProvider> & { saving: boolean };
    recaptcha: InstanceType<typeof VueRecaptcha> & { reset(): void };
  };

  @Prop({
    type: Object,
    default() {
      return {};
    },
  })
  readonly value: any;
  public showForm: boolean = false;
  public entityId: any;
  public isSavingForm: boolean = false;
  showAlertSuccess: boolean = false;
  recaptchaVerified: boolean = false;
  recaptchaCode: string = null;

  get savePath() {
    return `${this.$bpcApiRouteHelper.getEntityPath('contact')}/new`;
  }
  get fieldsConfig() {
    return newContactClubFields;
  }

  onEntitySaved(e) {
    this.$emit('entity-saved', e);
    this.showAlertSuccess = true;

    // Reset the form when it is saved
    Object.keys(this.formModel).forEach(key => {
      this.formModel[key] = null;
    });
  }

  showNewContactForm() {
    this.showAlertSuccess = false;
    this.recaptchaCode = null;
  }

  get useRecaptcha(): boolean {
    return !!process.env.VUE_APP_RECAPTCHA_SITE_KEY;
  }

  get recaptchaSiteKey(): string {
    return process.env.VUE_APP_RECAPTCHA_SITE_KEY || '6Le_IMcZAAAAAGFibon8pJAIB809CODlqFdHRowK';
  }

  get resetRecaptcha() {
    return this.$store.getters['auth/resetRecaptcha'];
  }

  onVerifyRecaptcha(response) {
    this.recaptchaCode = response;
    this.recaptchaVerified = true;
  }

  onExpiredRecaptcha() {
    this.recaptchaVerified = false;
    this.recaptchaCode = null;
  }

  onResetRecaptcha() {
    this.$refs.formProvider.saving = false;
    this.recaptchaVerified = false;
    this.recaptchaCode = null;
    this.$refs.recaptcha.reset();
    this.$store.commit('auth/restartedRecaptcha');
  }

  onSaveContact() {
    this.$set(this.formModel, 'recaptcha', this.recaptchaCode);
    this.$refs.formProvider.saveEntity(this.formModel);
  }

  onErrorSavingEntity($event) {
    this.$store.commit('auth/restartRecaptcha');
  }

  get col6() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-6 px-2': true,
        },
      },
    };
  }
  get col12() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-12 px-0': true,
        },
      },
    };
  }
  get col4() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-4 pl-0 pr-1': true,
        },
      },
    };
  }
  get col8() {
    return {
      wrapperAttributes: {
        class: {
          'inline-field col-md-8 pl-1 pr-0': true,
        },
      },
    };
  }

  onSavingEntity(event) {
    this.isSavingForm = event;
  }

  get newContactFormTitle() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].new_contact_form_title;
    }
    return null;
  }
  get newContactFormDescription() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].new_contact_form_description;
    }
    return null;
  }

  get centerSettings() {
    return this.$store.getters['centerSettings/centerSettings'];
  }

  get ageLimit() {
    return this.$store.getters['centerSettings/centerSettings']
      ? this.$store.getters['centerSettings/centerSettings'].age_limit
      : null;
  }

  checkAgeFromBirthdate() {
    let ageIsOverTheLimit = true;
    if (this.formModel.birthdate) {
      const birthdate = moment(this.formModel.birthdate, 'YYYY-MM-DD');
      const age = moment().diff(birthdate, 'years');
      ageIsOverTheLimit = age >= this.ageLimit;
    }
    return ageIsOverTheLimit;
  }

  get showRelation1Form() {
    const ageIsOverTheLimit = this.checkAgeFromBirthdate();
    return this.$store.getters['centerSettings/centerSettings']
      ? this.$store.getters['centerSettings/centerSettings'].show_new_contact_relation1_form &&
          !ageIsOverTheLimit
      : null;
  }

  get showRelation2Form() {
    const ageIsOverTheLimit = this.checkAgeFromBirthdate();
    return this.$store.getters['centerSettings/centerSettings']
      ? this.$store.getters['centerSettings/centerSettings'].show_new_contact_relation2_form &&
          !ageIsOverTheLimit
      : null;
  }

  get formModel(): any {
    return this.value;
  }
  set formModel(value) {
    this.$emit('input', value);
  }

  get enableSaveButton() {
    if (!this.formModel.initial_agreement) {
      return false;
    }
    if (this.useRecaptcha && !this.recaptchaCode) {
      return false;
    }
    return true;
  }

  onChangeContactRelation1Category(contact_relation1_category) {
    this.formModel.contact_relation1_category_id = contact_relation1_category?.id || null;
  }

  onChangeContactRelation2Category(contact_relation2_category) {
    this.formModel.contact_relation2_category_id = contact_relation2_category?.id || null;
  }

  @Watch('resetRecaptcha')
  onChangeResetRecaptcha() {
    if (this.resetRecaptcha === true) {
      this.onResetRecaptcha();
    }
  }
}
